import { ApiClient } from '@api/api-configuration';
import { captureError } from '@app/lib/Sentry';
import { Tag, message } from 'antd';
import dayjs from 'dayjs';
import { motion } from 'framer-motion';
import { FiAlertCircle, FiDownload } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { DataExportCompletedNotificationType, DataExportFailedNotificationType } from '../../types/notifications';
import { LIST_ITEM_ANIMATIONS } from '../consts/motionAnimations';

type DataExportWidgetNotificationCardProps = {
  notificationDetails: DataExportCompletedNotificationType | DataExportFailedNotificationType;
  onClick: (id: number) => void;
};

const DataExportWidgetNotificationCard = ({ notificationDetails, onClick }: DataExportWidgetNotificationCardProps) => {
  const { t } = useTranslation();
  const meta = notificationDetails.metadata?.[0];
  const timeAgo = (iso: string) => dayjs(iso).fromNow();
  const isSuccess = meta?.success !== false;
  const filePath = meta?.filePath;

  const handleDownload = async () => {
    if (!filePath) {
      message.error(t('notifications-download-no-file'));
      return;
    }

    // Determine which API to use based on the file path or notification type
    // File path pattern: /srv/app/mnt/exports/visits_export_*.csv or /srv/app/mnt/exports/service_requests_export_*.csv
    const isVisitExport =
      filePath.includes('visits_export') ||
      filePath.includes('/visits/') ||
      notificationDetails.title.toLowerCase().includes('visit');

    try {
      // Call the download API
      if (isVisitExport) {
        const response = await ApiClient.visitsApi.visits.downloadVisitExport({
          file_path: filePath,
        });
        const blob = new Blob([response.data], { type: 'text/csv' });
        triggerDownload(blob, filePath);
      } else {
        const response = await ApiClient.serviceRequestsApi.serviceRequests.downloadServiceRequestExport({
          file_path: filePath,
        });
        const blob = new Blob([response.data], { type: 'text/csv' });
        triggerDownload(blob, filePath);
      }

      message.success(t('notifications-download-success'));

      // Mark notification as read after successful download
      onClick(notificationDetails.notificationId);
    } catch (error) {
      captureError(error as Error, {
        context: 'Export file download',
        filePath,
        isVisitExport,
      });
      message.error(t('notifications-download-error'));
    }
  };

  const triggerDownload = (blob: Blob, path: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = path.split('/').pop() || 'export.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleClick = () => {
    if (isSuccess && filePath) {
      // For successful exports, we handle download separately
      return;
    }
    onClick(notificationDetails.notificationId);
  };

  return (
    <motion.li key={notificationDetails.notificationId} className="overflow-hidden" {...LIST_ITEM_ANIMATIONS}>
      <div className={`group flex flex-col px-4 py-3 hover:bg-slate-50 cursor-pointer`}>
        <div
          onClick={isSuccess && filePath ? handleDownload : handleClick}
          className="flex w-full items-start gap-3 text-left"
        >
          <div
            className={`flex h-8 w-8 items-center justify-center rounded-full ${
              isSuccess ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'
            }`}
          >
            {isSuccess ? <FiDownload fontSize={16} /> : <FiAlertCircle fontSize={16} />}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-baseline justify-between">
              <p className="truncate text-sm font-semibold text-slate-800">{notificationDetails.title}</p>
              <time className="ml-2 flex-shrink-0 text-xs text-slate-500">
                {timeAgo(notificationDetails.createdAt)}
              </time>
            </div>
            <p className="mt-1 text-xs text-slate-600 line-clamp-2">{notificationDetails.message}</p>

            <div className="mt-2 flex items-center gap-2">
              <Tag color={isSuccess ? 'success' : 'error'} className="text-xs">
                {isSuccess ? t('notifications-tag-export-ready') : t('notifications-tag-export-failed')}
              </Tag>
              {isSuccess && filePath && (
                <span className="text-xs text-blue-600">{t('notifications-click-to-download')}</span>
              )}
              {!notificationDetails.isRead && (
                <div className="h-2 w-2 rounded-full bg-blue-500" aria-label={t('notifications-new-badge')} />
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.li>
  );
};

export default DataExportWidgetNotificationCard;
